import type { Key } from "react";
import {
  Field,
  Listbox as HUIListbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { twMerge } from "tailwind-merge";

import { ErrorLabel, Label } from "~/ui";
import type { Styled } from "~/utils";

interface ListboxOption<T> {
  label: string;
  value: T;
}

interface ListboxProps<T> extends Styled {
  options: ListboxOption<T>[];
  label?: string;
  id: string;
  error?: string;
  placeholder?: string;
  value?: T;
  onChange: (v?: T) => void;
}

export const Listbox = <T extends Key | null | undefined>({
  options,
  label,
  id,
  error,
  placeholder,
  value,
  onChange,
}: ListboxProps<T>) => {
  const currentOption = value
    ? options.find((option) => option.value === value)
    : undefined;

  return (
    <Field disabled={!options.length} className="flex flex-col items-start">
      {label && (
        <Label htmlFor={id} className={twMerge("mb-2")}>
          {label}
        </Label>
      )}
      <HUIListbox
        value={currentOption}
        onChange={(option) => onChange(option?.value)}
      >
        <ListboxButton
          className={twMerge(
            "relative h-12 w-full rounded-lg border border-neutral-variant-60 bg-surface-container-low py-3 pl-3 pr-8 text-left text-base dark:border-neutral-variant-40 dark:bg-surface-container-low-dark",
            "text-secondary-20 hover:text-primary-70 dark:text-secondary dark:hover:text-primary-98",
            error && "border-error-70",
          )}
        >
          <span
            id={id}
            className={twMerge(!currentOption && "text-neutral-variant-60")}
          >
            {currentOption?.label ?? placeholder}
          </span>
          <ChevronDownIcon
            className="group pointer-events-none absolute right-2.5 top-3.5 size-5 text-primary"
            aria-hidden="true"
            strokeWidth={3}
          />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom"
          className={twMerge(
            "z-20 mt-3 max-h-combobox-values w-[var(--button-width)] overflow-y-auto rounded-2xl [--anchor-gap:var(--spacing-1)]",
            options.length > 3 && "h-combobox-values",
          )}
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option}
              className="group flex h-auto cursor-default select-none items-center border-b border-gray-600 bg-surface-container-low last:border-0 data-[focus]:bg-surface-container dark:border-neutral-variant-40 dark:bg-surface-container-low-dark"
            >
              <div className="flex size-full items-center gap-2 border-l-3 border-transparent p-5 pl-3 hover:border-info-80">
                <CheckIcon
                  className="invisible size-4 text-neutral-variant-20 group-data-[selected]:visible dark:text-primary-98"
                  strokeWidth={3}
                />
                <div className="text-sm/6 text-neutral-variant-20 dark:text-primary-98">
                  {option.label}
                </div>
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </HUIListbox>
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </Field>
  );
};
